import { useMemo, useState } from 'react'

import { KnockProvider } from '@knocklabs/react'
import {
  IconBell,
  IconCrane,
  IconMapPin,
  IconMenu2,
  IconNotebook,
  IconPennant,
  IconPlus,
  IconUserCircle,
  IconDeviceMobile,
  IconBook,
  IconSearch,
  IconSpeakerphone,
  IconSwitch3,
  IconAlertTriangle,
  IconListCheck,
  IconListSearch,
  IconBolt,
  IconBulb,
} from '@tabler/icons-react'
import Link from 'next/link'
import { useCookies } from 'react-cookie'
import styled, { css } from 'styled-components'

import BuilderSelectModal from '../BuilderSelectModal'
import { Button } from '../shared-components/BUI/Button'
import { Modal } from '../shared-components/BUI/Modal'
import {
  Popover,
  PopoverDivider,
  PopoverItem,
  PopoverItemNaked,
} from '../shared-components/BUI/Popover'
import { UserAvatar } from '../shared-components/UserAvatar'
import { CustomCursors } from '../util-components/CustomCursors'
import { isSafari, isIosDevice } from '../util-components/PWAInstallPrompt'
import { SuperAdminSupportOptions } from '../util-components/SuperAdminSupportOptions'

import { CommandKText } from './CommandKBar/CommandKText'
import { CorePageHeaderBreadcrumbs } from './CorePageHeaderBreadcrumbs'
import { InboxDrawer, InboxProvider, InboxUserMenuItem } from './CorePageInbox'
import { FeedbackSection } from './Feedback/FeedbackSection'

import { extraRoutes } from '@/config/constants/extraRoutes'
import { accountManagers } from '@/config/constants/roleGroups'
import config from '@/config/index'
import { useAuth } from '@/context/authContext'
import { useCommandKBar } from '@/context/commandKBarContext'
import { useProject } from '@/context/projectContext'
import { useSupport } from '@/context/supportContext'
import { useUser } from '@/context/userContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useEnabledModules } from '@/hooks/useEnabledModules'
import { useHasRoles } from '@/hooks/useHasRoles'
import { Role } from '@/modules/adminUsers/roles'
import TrialBanner from '@/modules/builder/TrialBanner'
import { getDataTags, routes } from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

import { BuildPassModule } from 'types/api.graphql'

export const HEADER_HEIGHT = 70
export const HEADER_HEIGHT_MOBILE = 56
export const MOBILE_BREADCRUMBS_HEIGHT = 56

export const Wrapper = styled.div`
  grid-area: header;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: 1030; // same as old Ant header

  ${({ theme: { media } }) => media.lg`
    position: relative;
  `}
`

export const Header = styled.header`
  width: 100%;
  max-width: 100vw;
  height: ${HEADER_HEIGHT_MOBILE}px;
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
  background-color: ${({ theme }) => theme.colour.neutral.white};

  ${({ theme: { media } }) => media.lg`
  height: ${HEADER_HEIGHT}px;
    background-color: transparent;
  `}
`

type ItemProps = {
  hideMobile?: boolean
}

export const HeaderItem = styled.div<ItemProps>`
  padding: 0.5rem;
  border-left: 1px solid ${({ theme }) => theme.colour.neutral.border};
  display: flex;
  align-items: center;

  &:first-child {
    border-left: none;
  }

  ${({ theme: { media } }) => media.lg`
    padding: 1rem;
  `}

  ${({ hideMobile, theme: { media } }) =>
    hideMobile &&
    css`
      display: none;

      ${media.lg`
        display: flex;
      `}
    `}
`

export const BreadcrumbsItem = styled(HeaderItem)`
  padding-left: 0; // let the back link set the padding
  flex-grow: 1;
  overflow: auto;
  direction: rtl;
`

const MobileBreadcrumbs = styled.div`
  grid-area: breadcrumbs;
  flex-grow: 1;
  padding: 1rem 1rem 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
  margin-bottom: -1px; // account for border
  z-index: 1; // account for border
  display: flex;
  align-items: center;
  overflow: auto;
  direction: rtl;
`

const MobileHeaderItem = styled(HeaderItem).attrs({ as: 'button' })`
  flex-grow: 1;
  flex-shrink: 1;
  padding-left: 1rem;
  padding-right: 0.25rem;
  border-top: none;
  border-right: none;
  border-bottom: none;
  background: transparent;
  text-align: left;
  color: ${({ theme }) => theme.colour.secondary._400};
  overflow: hidden;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colour.secondary._700};
  }
`

const Menu = styled(IconMenu2)`
  margin-right: 0.75rem;
`

const ProjectName = styled.span`
  ${({ theme }) => theme.font.label01}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._700};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LogoutWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  margin-bottom: 0.5rem;
  padding-left: 1.75rem;
  padding-right: 1.75rem;
`

const UserWrapper = styled(PopoverItemNaked)`
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
`

type Props = {
  openDrawer: () => void
  backUrl?: string
}

export const CorePageHeader: React.FC<Props> = ({ openDrawer, backUrl }) => {
  const isSmall = useBreakpoint('lg')
  const canAddProjects = useHasRoles(accountManagers, true)

  const isSuperAdmin = useHasRoles(Role.SuperAdmin, false)
  const { project } = useProject()
  const { isInSupportMode } = useSupport()
  const { user, userLoading } = useUser()
  const { organisations } = useAuth()
  const { openCommandKBar } = useCommandKBar()
  const { isModuleEnabled } = useEnabledModules()

  const [, , removeCookie] = useCookies(['ignoreHomeScreenPrompt'])

  const [isSelectBuilderModalOpen, setIsSelectBuilderModalOpen] =
    useState(false)

  const [isFeedbackSectionOpen, setIsFeedbackSectionOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)

  const newMenuActions = useMemo(() => {
    return [
      ...(isModuleEnabled(BuildPassModule.Actions)
        ? [
            {
              label: 'New Action',
              icon: IconBolt,
              url: routes.actions.new(),
            },
          ]
        : []),
      ...(canAddProjects
        ? [
            {
              label: 'New Project',
              icon: IconMapPin,
              url: routes.projects.new(),
            },
          ]
        : []),
      {
        label: 'New Company',
        icon: IconCrane,
        url: routes.subcontractors.list(),
      },
      ...(isModuleEnabled(BuildPassModule.SiteDiaries)
        ? [
            {
              label: 'New Site diary',
              icon: IconNotebook,
              url: routes.siteDiaries.new(),
            },
          ]
        : []),
      ...(isModuleEnabled(BuildPassModule.Defects)
        ? [
            {
              label: 'New Defect',
              icon: IconPennant,
              url: routes.defects.list({ showNewModal: true }),
            },
          ]
        : []),
      ...(isModuleEnabled(BuildPassModule.Checklists)
        ? [
            {
              label: 'New Form',
              icon: IconListCheck,
              url: routes.checklistsChecklists.newChecklist(),
            },
          ]
        : []),
      ...(isModuleEnabled(BuildPassModule.Itps)
        ? [
            {
              label: 'New Inspection',
              icon: IconListSearch,
              url: routes.itpsInspections.newITP(),
            },
          ]
        : []),
      ...(isModuleEnabled(BuildPassModule.Emergency)
        ? [
            {
              label: 'New Emergency',
              icon: IconAlertTriangle,
              url: routes.emergency.new(),
            },
          ]
        : []),
      ...(isModuleEnabled(BuildPassModule.PreStarts)
        ? [
            {
              label: 'New Notice',
              icon: IconSpeakerphone,
              url: routes.preStarts.new(),
            },
          ]
        : []),
    ].map(({ label, icon: Icon, url }) => (
      <Link href={url} key={label} passHref>
        <PopoverItem
          {...getDataTags({ name: `core-page-${label}`, type: 'button' })}
        >
          <Icon size={18} />
          {label}
        </PopoverItem>
      </Link>
    ))
  }, [canAddProjects, isModuleEnabled])

  const userMenuActions = useMemo(() => {
    if (userLoading) {
      return null
    }

    const triggerInstallPrompt = () => {
      removeCookie('ignoreHomeScreenPrompt')
    }

    return (
      <>
        <UserWrapper>
          <UserAvatar user={user} isInSupportMode={isInSupportMode} />
        </UserWrapper>
        {organisations?.length > 1 && !isSuperAdmin && (
          <>
            <PopoverDivider />
            <PopoverItem onClick={() => setIsSelectBuilderModalOpen(true)}>
              <IconSwitch3 size={16} />
              Switch company
            </PopoverItem>
          </>
        )}
        <PopoverDivider />
        <Link href={extraRoutes.account.details}>
          <PopoverItem as="span">
            <IconUserCircle size={18} />
            Account details
          </PopoverItem>
        </Link>
        <Link href={extraRoutes.account.cheatsheet}>
          <PopoverItem as="span">
            <IconBook size={18} />
            Cheat sheet
          </PopoverItem>
        </Link>
        <PopoverDivider />

        <InboxUserMenuItem />

        <Link href={extraRoutes.account.notificationSettings}>
          <PopoverItem as="span">
            <IconBell size={18} />
            Notification settings
          </PopoverItem>
        </Link>

        {/* TODO: remove this once we're confident no one is using a custom cursor :( */}
        <CustomCursors />

        <PopoverDivider />
        {isSmall && (
          <PopoverItem onClick={() => setIsFeedbackModalOpen(true)}>
            <IconBulb
              size={18}
              style={{ color: buildPassTheme.colour.primary._600 }}
            />
            <span style={{ color: buildPassTheme.colour.primary._600 }}>
              Feedback?
            </span>
          </PopoverItem>
        )}

        {isSuperAdmin ? <SuperAdminSupportOptions /> : null}

        {isIosDevice() && isSafari() && (
          <PopoverItem
            onClick={() => {
              triggerInstallPrompt()
            }}
          >
            <IconDeviceMobile size={18} />
            Install BuildPass app
          </PopoverItem>
        )}
        <LogoutWrapper>
          <Link href="/logout">
            <Button variant="secondary" block negative>
              Log out
            </Button>
          </Link>
        </LogoutWrapper>
      </>
    )
  }, [
    isInSupportMode,
    isSmall,
    isSuperAdmin,
    organisations?.length,
    removeCookie,
    user,
    userLoading,
  ])

  return (
    <KnockProvider apiKey={config.knock.publicApiKey} userId={user?.id}>
      <InboxProvider>
        <Wrapper id="bp-header">
          {isSmall && <TrialBanner />}
          <Header>
            {isSmall ? (
              <MobileHeaderItem onClick={openDrawer}>
                <Menu size={28} />
                <ProjectName>{project?.name}</ProjectName>
              </MobileHeaderItem>
            ) : (
              <BreadcrumbsItem>
                <CorePageHeaderBreadcrumbs backUrl={backUrl} />
              </BreadcrumbsItem>
            )}

            <HeaderItem
              style={
                !isSmall
                  ? {
                      width: '100%',
                      maxWidth: '156px',
                      justifyContent: 'center',
                    } // line up with the CorePageViewAndEditForm side panel
                  : {}
              }
            >
              <Button
                variant="secondary-text"
                icon={<IconSearch size={14} />}
                onClick={openCommandKBar}
              >
                {!isSmall && <CommandKText />}
              </Button>
            </HeaderItem>

            <HeaderItem>
              <Popover
                showArrow={false}
                content={newMenuActions}
                placement="bottomRight"
                getPopupContainer={() => document.getElementById('bp-header')}
              >
                <Button
                  variant="secondary-text"
                  {...getDataTags({ name: 'core-page-new', type: 'button' })}
                  icon={<IconPlus size={14} />}
                >
                  New
                </Button>
              </Popover>
            </HeaderItem>

            <HeaderItem hideMobile>
              <Popover
                style={{ width: '400px' }}
                content={
                  <FeedbackSection
                    onClose={() => setIsFeedbackSectionOpen(false)}
                  />
                }
                open={isFeedbackSectionOpen}
                onOpenChange={setIsFeedbackSectionOpen}
                showArrow={false}
              >
                <Button icon={<IconBulb size={12} />} variant="tertiary">
                  Feedback?
                </Button>
              </Popover>
            </HeaderItem>

            <HeaderItem data-testid="user-menu">
              <Popover
                showArrow={false}
                content={userMenuActions}
                placement="bottomRight"
                getPopupContainer={() => document.getElementById('bp-header')}
              >
                <UserAvatar
                  user={user}
                  isInSupportMode={isInSupportMode}
                  isButton
                />
              </Popover>
            </HeaderItem>
          </Header>
        </Wrapper>

        {isSmall && (
          <MobileBreadcrumbs>
            <CorePageHeaderBreadcrumbs backUrl={backUrl} />
          </MobileBreadcrumbs>
        )}

        {organisations.length > 1 && (
          <BuilderSelectModal
            open={isSelectBuilderModalOpen}
            closeModal={() => setIsSelectBuilderModalOpen(false)}
          />
        )}

        <Modal
          open={isFeedbackModalOpen}
          onCancel={() => setIsFeedbackModalOpen(false)}
          closable={true}
          fullscreen
          title="Have feedback?"
        >
          {isFeedbackModalOpen && (
            <FeedbackSection onClose={() => setIsFeedbackModalOpen(false)} />
          )}
        </Modal>

        <InboxDrawer />
      </InboxProvider>
    </KnockProvider>
  )
}
